import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { ErrorMessage, Validate, ValidateGroup } from '../../../../../components/Validation';
import Textbox from '../../../components/Textbox';
import OptionsContainer from '../../../components/OptionsContainer';
import PageSectionHeader from '../../../components/PageSectionHeader';
import messages from '../../messages';

/**
 * Renders a dynamic list of payment form fields based on configuration from the API.
 * Each field shape: { id, fieldName, fieldLabel, uiControl, tooltip, isCodeCheck, value, order, validateRule }
 * validateRule shape: { isMandatory, isOnlyDigits, maxLength, minLength }
 */
class DynamicPaymentFormFields extends React.Component {
  buildValidators(field) {
    const validators = [];
    const { validateRule } = field;
    if (!validateRule) return validators;

    if (validateRule.isMandatory) {
      validators.push(function isRequired(value) {
        if (value === undefined || value === null || value === '' || value === false) {
          return false;
        }
        return true;
      });
    }

    if (validateRule.isOnlyDigits) {
      validators.push(function isOnlyDigits(value) {
        if (!value && !validateRule.isMandatory) return true;
        return /^\d+$/.test(String(value));
      });
    }

    if (validateRule.minLength) {
      const min = validateRule.minLength;
      validators.push(function isMinLength(value) {
        if (!value && !validateRule.isMandatory) return true;
        return String(value || '').length >= min;
      });
    }

    if (validateRule.maxLength) {
      const max = validateRule.maxLength;
      validators.push(function isMaxLength(value) {
        if (!value) return true;
        return String(value || '').length <= max;
      });
    }

    return validators;
  }

  renderTextField(field, value, isReadOnly, validatorGroup, validationCounter) {
    const { onChange, onCodeFieldBlur } = this.props;
    const validators = this.buildValidators(field);
    const { validateRule } = field;

    // Trigger code lookup on blur/change only for:
    // 1. field.isCodeCheck is explicitly true, OR
    // 2. field is BankBranchCode (transit number) — backend may not set isCodeCheck=true
    const isLookupField = field.isCodeCheck === true || field.fieldName === 'BankBranchCode';

    const handleBlur = isLookupField && onCodeFieldBlur
      ? (e) => { if (e.target.value) onCodeFieldBlur(field.fieldName, e.target.value); }
      : undefined;

    // Also trigger immediately when the value reaches the required fixed length (on change)
    const handleChange = (e) => {
      const v = e.target.value;
      onChange(field.fieldName, v);
      if (isLookupField && onCodeFieldBlur && validateRule && validateRule.minLength > 0 && v.length === validateRule.minLength) {
        onCodeFieldBlur(field.fieldName, v);
      }
    };

    return (
      <div className={'col-md-6 form-group mb02'} key={field.id}>
        {validators.length > 0 ? (
          <Validate
            name={field.fieldName}
            group={validatorGroup}
            validators={validators}
            addValidatorToGroup={validatorGroup.addValidatorToGroup}
            validChangeInGroup={validatorGroup.validChangeInGroup}
            validationCounter={validationCounter}
            validatorLeaveGroup={validatorGroup.removeValidatorToGroup}
            fieldName={{ id: field.fieldName, defaultMessage: field.fieldLabel }}
            tooltipContent={field.tooltip || null}
          >
            <Textbox
              disabled={isReadOnly}
              name={field.fieldName}
              type={validateRule && validateRule.isOnlyDigits ? 'number' : 'text'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={value || ''}
            />
            {validators.map((validator) => {
              let formattedMessage;
              switch (validator.name) {
                case 'isRequired':
                  formattedMessage = <FormattedMessage {...messages.isRequired} values={{ defaultMessage: field.fieldLabel }} />;
                  break;
                case 'isOnlyDigits':
                  formattedMessage = <FormattedMessage {...messages.isOnlyDigits} values={{ defaultMessage: field.fieldLabel }} />;
                  break;
                case 'isMinLength':
                  formattedMessage = <FormattedMessage {...messages.isMinLength} values={{ defaultMessage: field.fieldLabel, minLength: validateRule.minLength }} />;
                  break;
                case 'isMaxLength':
                  formattedMessage = <FormattedMessage {...messages.isMaxLength} values={{ defaultMessage: field.fieldLabel }} />;
                  break;
                default:
                  formattedMessage = field.fieldLabel;
              }
              return (
                <ErrorMessage key={validator.name} for={validator}>
                  {formattedMessage}
                </ErrorMessage>
              );
            })}
          </Validate>
        ) : (
          <div>
            <PageSectionHeader>{field.fieldLabel}</PageSectionHeader>
            <Textbox
              disabled={isReadOnly}
              name={field.fieldName}
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              value={value || ''}
            />
          </div>
        )}
        {field.tooltip && (
          <p style={{ fontSize: '0.8em', color: '#555', marginTop: 2 }}>
            {field.tooltip}
          </p>
        )}
      </div>
    );
  }

  renderCheckboxField(field, value, isReadOnly) {
    const { onChange } = this.props;
    return (
      <div className={'col-md-12 form-group mb02'} key={field.id}>
        <OptionsContainer style={{ marginBottom: 0 }}>
          <input
            disabled={isReadOnly}
            type="checkbox"
            name={field.fieldName}
            checked={!!value}
            onChange={(e) => onChange(field.fieldName, e.target.checked)}
          />
          <span>{field.fieldLabel}</span>
        </OptionsContainer>
      </div>
    );
  }

  render() {
    const { fields, formValues, isReadOnly, validatorGroup, validationCounter } = this.props;

    if (!fields || fields.length === 0) {
      return null;
    }

    const sortedFields = [...fields].sort((a, b) => a.order - b.order);
    const isInvalidBsbChecked = !!formValues['IsInvalidBsb'];

    return (
      <div>
        {sortedFields.map((field) => {
          const value = formValues[field.fieldName];
          // IsInvalidBsb checkbox: always interactive
          // Code-check fields (SWIFT/Transit): enabled when IsInvalidBsb is NOT checked
          // Other fields: enabled only when IsInvalidBsb IS checked
          const isCheckboxControl = field.fieldName === 'IsInvalidBsb';
          const isCodeCheckField = field.isCodeCheck === true || field.fieldName === 'BankBranchCode';
          let fieldDisabled;
          if (isCheckboxControl) {
            fieldDisabled = isReadOnly;
          } else if (isCodeCheckField) {
            fieldDisabled = isReadOnly || isInvalidBsbChecked;
          } else {
            fieldDisabled = isReadOnly || !isInvalidBsbChecked;
          }

          if (field.uiControl === 'checkbox') {
            return (
              <div className="row" key={field.id}>
                {this.renderCheckboxField(field, value, fieldDisabled)}
              </div>
            );
          }
          return (
            <div className="row" key={field.id}>
              {this.renderTextField(field, value, fieldDisabled, validatorGroup, validationCounter)}
            </div>
          );
        })}
      </div>
    );
  }
}

DynamicPaymentFormFields.propTypes = {
  fields: PropTypes.array,
  formValues: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onCodeFieldBlur: PropTypes.func,
  isReadOnly: PropTypes.bool,
  validatorGroup: PropTypes.object.isRequired,
  validationCounter: PropTypes.number,
};

DynamicPaymentFormFields.defaultProps = {
  fields: [],
  formValues: {},
  isReadOnly: false,
  validationCounter: 0,
};

export default DynamicPaymentFormFields;



// WEBPACK FOOTER //
// ./app/containers/FastCover/Appsure.StepBankDetails/components/DynamicPaymentFormFields/index.js